import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { MessageSquareText, Tags, Users, LogOut, Menu, X, BookUser, History, AlarmClock, Bell, Plane } from "lucide-react";
import { ReminderPopup } from "@/components/ReminderPopup";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, isAdminRole } from "@/lib/roles";
import { LOGOUT, SHELL } from "@/constants/testIds";

const NAV = [
  { to: "/balasan", label: "Balasan Cepat", icon: MessageSquareText, testid: SHELL.navQuickReply },
  { to: "/cek-paspor", label: "Cek Masa Berlaku Paspor", icon: BookUser, testid: SHELL.navPassport },
  { to: "/booking", label: "Reminder Booking", icon: AlarmClock, testid: SHELL.navBookings },
  { to: "/status-seat", label: "Status Seat", icon: Plane, testid: "nav-seat-status-link" },
  { to: "/kategori", label: "Kelola Kategori", icon: Tags, testid: SHELL.navCategories, admin: true },
  { to: "/pengguna", label: "Kelola Pengguna", icon: Users, testid: SHELL.navUsers, admin: true },
  { to: "/riwayat", label: "Riwayat Perubahan", icon: History, testid: SHELL.navAudit, admin: true },
  { to: "/notifikasi", label: "Pengaturan Notifikasi", icon: Bell, testid: SHELL.navNotify, superadmin: true },
];

const Sidebar = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col bg-white">
      <div className="px-6 py-6 border-b border-slate-200">
        <img src="/logo.png" alt="Arumichi Travel" className="h-9 w-auto" />
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        <p className="overline-label px-3 pb-3">Program</p>
        {NAV.filter((n) => (!n.admin || isAdminRole(user.role)) && (!n.superadmin || user.role === "superadmin")).map((n) => (
          <NavLink key={n.to} to={n.to} end onClick={onNavigate} data-testid={n.testid}
            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-50 text-brand font-semibold text-sm">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-900" data-testid={SHELL.userName}>{user.name}</p>
            <p className="text-xs text-slate-500" data-testid={SHELL.userRole}>{ROLE_LABEL[user.role]}</p>
          </div>
          <button onClick={logout} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Keluar" data-testid={LOGOUT.button}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const AppShell = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-200 lg:block">
        <Sidebar />
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
        <img src="/logo.png" alt="Arumichi Travel" className="h-7 w-auto" />
        <button onClick={() => setOpen(true)} className="rounded-md p-2 text-slate-600 hover:bg-slate-100" data-testid={SHELL.mobileMenu} aria-label="Menu">
          <Menu className="h-5 w-5" />
        </button>
      </header>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="relative h-full w-72 shadow-xl">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-5 z-10 rounded-md p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Tutup">
              <X className="h-4 w-4" />
            </button>
            <Sidebar onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-8 lg:px-10 lg:py-10">
          <Outlet />
        </div>
      </main>
      <ReminderPopup />
    </div>
  );
};
